import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { absoluteUrl } from '../lib/seo';

const faqs = [
  {
    question: "Comment se déroule un projet avec Couteau Suisse Digital ?",
    answer: "Le projet commence par un cadrage ou un appel de decouverte pour comprendre vos enjeux, le niveau d execution attendu et la meilleure structure de mission. Nous proposons ensuite un devis clair, un planning et des jalons de validation. Pendant la mission, vous gardez de la visibilite sur l avancement, les arbitrages et les prochaines etapes."
  },
  {
    question: "Quels sont les délais moyens de réalisation ?",
    answer: "Les delais varient selon la complexite et le niveau d ambition. Un site premium prend generalement 4 a 6 semaines, un e-commerce 6 a 10 semaines, et une plateforme sur mesure 8 a 14 semaines. Nous definissons toujours un planning realiste au demarrage."
  },
  {
    question: "Comment fonctionne l option Klarna ?",
    answer: "Klarna peut etre propose en 3 fois sans frais sur certains projets eligibles, uniquement jusqu a 2000 EUR. Au-dela, nous passons sur un paiement classique, un devis ou une solution de financement adaptee."
  },
  {
    question: "Proposez-vous un support après la livraison ?",
    answer: "Oui, nous offrons 30 jours de support gratuit après la livraison. Ensuite, vous pouvez souscrire à l'un de nos forfaits de maintenance qui incluent les mises à jour, la sécurité et le support technique."
  },
  {
    question: "Puis-je modifier mon site moi-même ?",
    answer: "Oui, tous nos sites sont livrés avec un CMS (système de gestion de contenu) intuitif. Nous vous formons à son utilisation et fournissons une documentation détaillée. Vous pouvez ainsi modifier vos contenus en toute autonomie."
  },
  {
    question: "Comment assurez-vous la sécurité des données ?",
    answer: "Nous appliquons les meilleures pratiques de sécurité : hébergement sécurisé, certificats SSL, sauvegardes régulières, protection contre les attaques DDoS. Nous sommes également conformes au RGPD."
  },
  {
    question: "Que se passe-t-il si je ne suis pas satisfait ?",
    answer: "Votre satisfaction est notre priorité. Nous travaillons de manière itérative et vous validez chaque étape. Si quelque chose ne vous convient pas, nous effectuons les modifications nécessaires jusqu'à votre entière satisfaction."
  },
  {
    question: "Proposez-vous des services de marketing digital ?",
    answer: "Oui, nous offrons une gamme complète de services marketing : SEO, publicité en ligne, réseaux sociaux, email marketing, etc. Nous pouvons créer une stratégie marketing complète ou intervenir sur des besoins spécifiques."
  }
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Couteau Suisse Digital</title>
        <meta name="description" content="Questions fréquentes sur les services de Couteau Suisse Digital" />
        <link rel="canonical" href={absoluteUrl('/faq')} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl font-bold mb-4">Questions Fréquentes</h1>
            <p className="text-xl text-gray-600">
              Trouvez rapidement des réponses à vos questions
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                  aria-expanded={openIndex === index}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <svg
                    className={`w-6 h-6 transform transition-transform ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                <motion.div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  aria-hidden={openIndex !== index}
                  initial={false}
                  animate={{
                    height: openIndex === index ? 'auto' : 0,
                    opacity: openIndex === index ? 1 : 0
                  }}
                  className="overflow-hidden"
                >
                  <div className="p-6 pt-0 text-gray-600">
                    {faq.answer}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-6">
              Vous n'avez pas trouvé la réponse à votre question ?
            </p>
            <a
              href="/contact"
              className="inline-block bg-neon-orange text-white px-8 py-4 rounded-full font-semibold hover:bg-[#FF4500] transition-colors duration-300"
            >
              Contactez-nous
            </a>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default FAQ;
